import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useCreateLink,
  useDeleteLink,
  useLinks,
} from '../api/hooks.js';
import LinkForm from '../components/LinkForm.js';
import Modal from '../components/Modal.js';
import { shortUrlFor } from '../lib/config.js';
import { compactNumber, formatRelative } from '../lib/format.js';
import { useCanManageLink } from '../lib/perms.js';
import { ApiError } from '../api/client.js';
import { useWorkspaceId } from '../workspace.js';

export default function Dashboard() {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const { data: links, isLoading } = useLinks(query);
  const create = useCreateLink();
  const del = useDeleteLink();
  const canManage = useCanManageLink();
  const workspaceId = useWorkspaceId();

  async function downloadCsv() {
    if (!workspaceId) return;
    // Use fetch directly so we can stream the response into a Blob — the
    // shared `api()` helper assumes JSON.
    const res = await fetch('/api/links/export.csv', {
      credentials: 'include',
      headers: { 'x-workspace-id': workspaceId },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `adhc-links-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Links</h1>
          <p className="mt-1 text-sm text-slate-500">
            Create, manage, and measure your short links.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <button className="btn-secondary" onClick={downloadCsv} disabled={!links || links.length === 0}>
            Export CSV
          </button>
          <button className="btn-primary" onClick={() => { setFormError(null); setOpen(true); }}>
            + New link
          </button>
        </div>
      </div>

      <div className="mt-6">
        <input
          className="input sm:max-w-md"
          placeholder="Search by slug, destination, or title…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : !links || links.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            No links yet. Tap <span className="font-medium">+ New link</span> to create one.
          </div>
        ) : (
          <>
            {/* Mobile: stacked cards. Tables don't reflow under 600px. */}
            <ul className="divide-y divide-slate-100 sm:hidden">
              {links.map((l) => (
                <li key={l.id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <Link
                      to={`/app/links/${l.id}`}
                      className="min-w-0 flex-1"
                    >
                      <p className="font-mono text-base text-brand-700">{l.slug}</p>
                      {l.title && (
                        <p className="mt-0.5 truncate text-sm text-slate-700">{l.title}</p>
                      )}
                      <p className="mt-0.5 truncate text-xs text-slate-500" title={l.destination_url}>
                        {l.destination_url}
                      </p>
                    </Link>
                    <CopyButton text={shortUrlFor(l.slug)} />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                    <span>
                      <span className="font-mono text-slate-700">{compactNumber(l.click_count)}</span>{' '}
                      clicks · {formatRelative(l.created_at)}
                    </span>
                    {canManage(l) ? (
                      <button
                        className="text-red-600 hover:underline"
                        onClick={() => {
                          if (confirm(`Delete adhc.to/${l.slug}?`)) {
                            del.mutate(l.id);
                          }
                        }}
                      >
                        Delete
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>

            {/* sm+ : the original table view. */}
            <table className="hidden min-w-full divide-y divide-slate-200 text-sm sm:table">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Short link</th>
                  <th className="px-4 py-3 font-medium">Destination</th>
                  <th className="px-4 py-3 font-medium text-right">Clicks</th>
                  <th className="px-4 py-3 font-medium">Created</th>
                  <th className="px-4 py-3 font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {links.map((l) => (
                  <tr key={l.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/app/links/${l.id}`}
                          className="font-mono text-brand-700 hover:underline"
                        >
                          {l.slug}
                        </Link>
                        <CopyButton text={shortUrlFor(l.slug)} />
                      </div>
                      {l.title && <p className="mt-0.5 text-xs text-slate-500">{l.title}</p>}
                    </td>
                    <td className="max-w-xs px-4 py-3">
                      <a
                        href={l.destination_url}
                        className="truncate text-slate-600 hover:text-slate-900"
                        target="_blank"
                        rel="noreferrer"
                        title={l.destination_url}
                      >
                        <span className="block truncate">{l.destination_url}</span>
                      </a>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-700">
                      {compactNumber(l.click_count)}
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {formatRelative(l.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canManage(l) ? (
                        <button
                          className="text-xs text-red-600 hover:underline"
                          onClick={() => {
                            if (confirm(`Delete adhc.to/${l.slug}?`)) {
                              del.mutate(l.id);
                            }
                          }}
                        >
                          Delete
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400" title="Only the link's creator (or an admin) can delete it">
                          —
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="New short link">
        <LinkForm
          submitLabel="Create link"
          busy={create.isPending}
          error={formError}
          onCancel={() => setOpen(false)}
          onSubmit={async (values) => {
            setFormError(null);
            try {
              await create.mutateAsync(values);
              setOpen(false);
            } catch (e) {
              if (e instanceof ApiError) {
                const err = (e.payload as { error?: string })?.error ?? 'request_failed';
                setFormError(prettyError(err));
              } else {
                setFormError('Something went wrong.');
              }
            }
          }}
        />
      </Modal>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="rounded border border-slate-200 px-1.5 py-0.5 text-xs text-slate-500 hover:bg-slate-50"
      onClick={(e) => {
        e.preventDefault();
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function prettyError(code: string): string {
  switch (code) {
    case 'slug_taken': return 'That slug is already in use.';
    case 'slug_reserved': return 'That slug is reserved.';
    case 'invalid_expiration': return 'Expiration must be in the future.';
    default: return code.replace(/_/g, ' ');
  }
}
